import { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Snackbar } from '@mui/material';
import { useAuthUserStore, useNetworkErrorStore, usePartnersStore } from '@lib';
import { AuthApi } from '@shared/api';
import { Private } from '@routes';
import { AuthUtils } from '@shared/utils';
import type { AuthUser } from '@shared/types';

export const App = () => {
  const { authUser } = useAuthUserStore();
  const { networkError } = useNetworkErrorStore();
  const { getPartners } = usePartnersStore();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuth, setIsAuth] = useState<boolean>(AuthUtils.isAuth());

  const onCallbackAuthEvent = (isAuthTmp: boolean) => {
    setIsAuth(isAuthTmp);
    if (!isAuthTmp) return;
    getPartners();
  };

  useEffect(() => {
    (async () => {
      onCallbackAuthEvent(isAuth);

      AuthUtils.eventIsAuth.on(AuthUtils.isAuthEventName, onCallbackAuthEvent);

      AuthUtils.init();

      if (!AuthUtils.isAuth()) {
        const userId = AuthUtils.getTempTokenForLogIn();
        if (userId) {
          try {
            setIsLoading(true);
            const authUser = await AuthApi.authenticate(userId);
            if (!authUser) return;
            AuthUtils.updateAuthData(authUser as unknown as AuthUser);
          } catch {
            AuthUtils.logOut();
          } finally {
            setIsLoading(false);
          }
        } else {
          AuthUtils.redirectToLoginPage();
          setIsLoading(false);
        }
      } else {
        AuthUtils.eventIsAuth.emit(AuthUtils.isAuthEventName, true);
        setIsLoading(false);
      }
    })();

    return () => {
      AuthUtils.eventIsAuth.off(AuthUtils.isAuthEventName, onCallbackAuthEvent);
    };
  }, []);

  if (networkError) {
    return (
      <Snackbar open anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert elevation={6} variant='filled' severity='error'>
          Server is unreachable. Please try again later.
        </Alert>
      </Snackbar>
    );
  }
  if (!isLoading && authUser == null) {
    return (
      <Snackbar open anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={6000}>
        <Alert elevation={6} variant='filled' severity='error'>
          No login details provided
        </Alert>
      </Snackbar>
    );
  }
  if (isLoading) {
    return (
      <Box sx={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  return <Private />;
};
